import {
  collection, doc, getDoc, getDocs, query, where, addDoc,
  updateDoc, deleteDoc, serverTimestamp, orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, COLLECTIONS } from './config';

// Create a lesson post (teacher only)
export const createLessonPost = async (teacherId, lessonData) => {
  try {
    const lessonRef = await addDoc(collection(db, 'lesson_posts'), {
      teacherId,
      title: lessonData.title,
      description: lessonData.description,
      content: lessonData.content,
      language: lessonData.language || '',
      attachments: lessonData.attachments || [],
      videoUrl: lessonData.videoUrl || '',
      type: lessonData.type || 'lesson', // lesson, exercise, resource
      status: 'published',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: lessonRef.id };
  } catch (error) {
    console.error('Error creating lesson post:', error);
    throw error;
  }
};

// Upload attachment for a lesson
export const uploadLessonAttachment = async (teacherId, file) => {
  try {
    const fileRef = ref(storage, `lesson_posts/${teacherId}/${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);
    return { name: file.name, url, type: file.type, size: file.size };
  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw error;
  }
};

// Get lessons posted by a teacher (teacher's own view)
export const getTeacherLessonPosts = async (teacherId) => {
  try {
    const q = query(
      collection(db, 'lesson_posts'),
      where('teacherId', '==', teacherId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
  } catch (error) {
    console.error('Error getting teacher lesson posts:', error);
    throw error;
  }
};

// Get lessons for a student (only from teachers they have bookings with)
export const getStudentLessonPosts = async (studentId) => {
  try {
    // First get all teachers this student has bookings with
    const bookingsQuery = query(
      collection(db, COLLECTIONS.BOOKINGS),
      where('studentId', '==', studentId),
      where('status', 'in', ['confirmed', 'completed'])
    );
    const bookingsSnap = await getDocs(bookingsQuery);
    const teacherIds = [...new Set(bookingsSnap.docs.map(d => d.data().teacherId))];

    if (teacherIds.length === 0) return [];

    // Get lessons from those teachers
    const allLessons = [];
    // Firestore 'in' supports max 10 values, so chunk
    for (let i = 0; i < teacherIds.length; i += 10) {
      const chunk = teacherIds.slice(i, i + 10);
      const lessonsQuery = query(
        collection(db, 'lesson_posts'),
        where('teacherId', 'in', chunk),
        where('status', '==', 'published')
      );
      const lessonsSnap = await getDocs(lessonsQuery);
      lessonsSnap.docs.forEach(d => allLessons.push({ id: d.id, ...d.data() }));
    }

    // Sort by date
    return allLessons.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting student lesson posts:', error);
    throw error;
  }
};

// Update a lesson post
export const updateLessonPost = async (lessonId, updateData) => {
  try {
    await updateDoc(doc(db, 'lesson_posts', lessonId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating lesson post:', error);
    throw error;
  }
};

// Delete a lesson post
export const deleteLessonPost = async (lessonId) => {
  try {
    await deleteDoc(doc(db, 'lesson_posts', lessonId));
    return true;
  } catch (error) {
    console.error('Error deleting lesson post:', error);
    throw error;
  }
};

// Get a single lesson post
export const getLessonPost = async (lessonId) => {
  try {
    const docSnap = await getDoc(doc(db, 'lesson_posts', lessonId));
    if (!docSnap.exists()) throw new Error('Lesson not found');
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting lesson post:', error);
    throw error;
  }
};
